/* eslint-disable @typescript-eslint/no-namespace */
import { Action, createSelector, Selector, State, StateContext, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import {
    EmissionFactorDatabaseDto,
    EmissionFactorHierarchyDto,
} from 'app/@types/dto/emission-factor-hierarchical-structure-dto.model';
import { EmissionFactorHttpService } from '@services/datasources/emission-factor-http.service';
import { OfficialCategoriesHttpService } from '@services/datasources/official-categories-http.service';
import { OfficialCategoriesByProtocolDto } from 'app/@types/dto/official-categories-by-protocol-dto.model';
import { startWith } from 'rxjs/operators';
import { AuthenticationService } from 'app/auth/service';
import { EmissionFactorDto } from 'app/@types/dto/emission-factor-dto.model';
import { Subject } from 'rxjs';

export interface EmissionFactorItemsModel {
    emissionsFactorsByDimension: { [dimensionId: string]: EmissionFactorHierarchyDto };
    isLoadingByDimension: { [dimensionId: string]: boolean };
    officialCategories: OfficialCategoriesByProtocolDto | undefined;
    emissionFactorByDatabase: { [databaseId: string]: EmissionFactorDatabaseDto };
    emissionFactorDatabases: EmissionFactorDatabaseDto[];
}

// Actions namespace
export namespace EmissionFactorItemsAction {
    export class SetEmissionFactorsByDimension {
        public static type = '[Emission Factor Items] Activate';
        constructor(
            public organizationId: string,
            public dimension: string,
            public lang: string,
        ) {}
    }

    export class SetEmissionFactorByDatabase {
        public static type = '[Emission Factor Items] Set Emission Factor By Database';
        constructor(
            public databaseId: string,
            public lang: string,
            public organizationId?: string,
        ) {}
    }

    export class InvalidateSpecificDatabase {
        public static type = '[Emission Factor Items] Invalidate Specific Database';
        constructor(public databaseId: string) {}
    }

    export class InvalidateAllDatabases {
        public static type = '[Emission Factor Items] Invalidate All Databases';
    }

    export class InvalidateSpecificDimension {
        public static type = '[Emission Factor Items] Invalidate Specific Dimension';
        constructor(public dimension: string) {}
    }

    export class InvalidateData {
        public static type = '[Emission Factor Items] Invalidate Data';
    }

    export class SetOfficialCategories {
        public static type = '[Emission Factor Items] Set Official Categories';
    }

    export class CreateEmissionFactor {
        public static type = '[Emission Factor Items] Create Emission Factor';
        constructor(
            public emissionFactor: EmissionFactorDto,
            public databaseId: string,
            public locale: string,
            public callCompletion: Subject<void>,
            public organizationId?: string,
        ) {}
    }

    export class UpdateEmissionFactor {
        public static type = '[Organization Items] Update Emission Factor';
        constructor(
            public emissionFactor: EmissionFactorDto,
            public databaseId: string,
            public locale: string,
            public callCompletion: Subject<void>,
            public organizationId?: string,
        ) {}
    }

    export class DeleteEmissionFactor {
        public static type = '[Organization Items] Delete Emission Factor';
        constructor(
            public emissionFactorId: string,
            public databaseId: string,
            public locale: string,
            public rootDimension: string,
            public callCompletion: Subject<void>,
            public organizationId?: string,
        ) {}
    }

    export class DeleteDimension {
        public static type = '[Organization Items] Delete Dimension';
        constructor(
            public dimensionId: string,
            public databaseId: string,
            public locale: string,
            public callCompletion: Subject<void>,
            public organizationId?: string,
        ) {}
    }

    export class CreateDimension {
        public static type = '[Organization Items] Create Dimension';
        constructor(
            public dimension: EmissionFactorHierarchyDto,
            public locale: string,
            public callCompletion: Subject<void>,
            public organizationId?: string,
        ) {}
    }

    export class UpdateDimension {
        public static type = '[Organization Items] Update Dimension';
        constructor(
            public dimension: EmissionFactorHierarchyDto,
            public locale: string,
            public rootDimension: string,
            public callCompletion: Subject<void>,
            public organizationId?: string,
        ) {}
    }

    export class SetEmissionFactorDatabases {
        public static type = '[Emission Factor Items] Set Emission Factor Databases';
        constructor(public organizationId?: string) {}
    }

    export class CreateEmissionFactorDatabase {
        public static type = '[Organization UI] Create an emission factor database';

        constructor(
            public organizationId: string,
            public database: EmissionFactorDatabaseDto,
            public callCompletion: Subject<void>,
        ) {}
    }

    export class UpdateEmissionFactorDatabase {
        public static type = '[Organization UI] Update an emission factor database';

        constructor(
            public organizationId: string,
            public database: EmissionFactorDatabaseDto,
            public callCompletion: Subject<void>,
        ) {}
    }

    export class DeleteEmissionFactorDatabase {
        public static type = '[Organization UI] Delete an emission factor database';

        constructor(
            public organizationId: string,
            public databaseId: string,
            public callCompletion: Subject<void>,
        ) {}
    }
}

@State<EmissionFactorItemsModel>({
    name: 'organizationItems',
    defaults: {
        emissionsFactorsByDimension: {},
        isLoadingByDimension: {},
        officialCategories: undefined,
        emissionFactorByDatabase: {},
        emissionFactorDatabases: [],
    },
})
@Injectable()
export class EmissionFactorItemsState {
    constructor(
        private translateService: CustomTranslateService,
        private store: Store,
        private emissionFactorHttpService: EmissionFactorHttpService,
        private officialCategoriesHttpService: OfficialCategoriesHttpService,
        private authenticationService: AuthenticationService,
    ) {
        this.translateService.onLangChange.pipe(startWith(null)).subscribe(() => {
            this.store.dispatch(new EmissionFactorItemsAction.InvalidateData());
            this.store.dispatch(new EmissionFactorItemsAction.SetOfficialCategories());
        });
    }

    @Action(EmissionFactorItemsAction.SetEmissionFactorsByDimension)
    setEmissionFactorsByDimension(
        ctx: StateContext<EmissionFactorItemsModel>,
        action: EmissionFactorItemsAction.SetEmissionFactorsByDimension,
    ) {
        //Is loading by dimension is important to avoid multiple calls to the API. ()
        if (ctx.getState().isLoadingByDimension[action.dimension]) {
            return;
        }
        ctx.patchState({
            isLoadingByDimension: {
                ...ctx.getState().isLoadingByDimension,
                [action.dimension]: true,
            },
        });
        //If the state contains a value for that dimension, it means that the state is valid.
        if (ctx.getState().emissionsFactorsByDimension[action.dimension]) {
            ctx.patchState({
                isLoadingByDimension: {
                    ...ctx.getState().isLoadingByDimension,
                    [action.dimension]: false,
                },
            });
            return;
        }
        this.emissionFactorHttpService
            .getEmissionFactor(action.organizationId, action.dimension, action.lang)
            .subscribe({
                next: (emissionFactors) => {
                    ctx.patchState({
                        emissionsFactorsByDimension: {
                            ...ctx.getState().emissionsFactorsByDimension,
                            [action.dimension]: emissionFactors,
                        },
                    });
                    ctx.patchState({
                        isLoadingByDimension: {
                            ...ctx.getState().isLoadingByDimension,
                            [action.dimension]: false,
                        },
                    });
                },
                error: () => {
                    ctx.patchState({
                        isLoadingByDimension: {
                            ...ctx.getState().isLoadingByDimension,
                            [action.dimension]: false,
                        },
                    });
                },
            });
    }

    @Action(EmissionFactorItemsAction.SetEmissionFactorByDatabase)
    setEmissionFactorByDatabase(
        ctx: StateContext<EmissionFactorItemsModel>,
        action: EmissionFactorItemsAction.SetEmissionFactorByDatabase,
    ) {
        //If the state contains a value for that dimension, it means that the state is valid.
        if (ctx.getState().emissionFactorByDatabase[action.databaseId]) {
            return;
        }
        this.emissionFactorHttpService
            .getEmissionFactorByDatabaseId(action.databaseId, action.lang, action.organizationId)
            .subscribe((emissionFactor) => {
                ctx.patchState({
                    emissionFactorByDatabase: {
                        ...ctx.getState().emissionFactorByDatabase,
                        [action.databaseId]: emissionFactor,
                    },
                });
            });
    }

    @Action(EmissionFactorItemsAction.SetOfficialCategories)
    setOfficialCategories(ctx: StateContext<EmissionFactorItemsModel>) {
        this.authenticationService.isLoggedIn(false).subscribe((isLoggedIn) => {
            if (isLoggedIn) {
                const lang = this.translateService.currentLang;
                this.officialCategoriesHttpService.getAllOfficialCategories(lang).subscribe((categories) => {
                    if (categories) {
                        ctx.patchState({
                            officialCategories: categories,
                        });
                    }
                });
            }
        });
    }

    /**
     *
     * @param ctx Used to invalidate the state when changing the language
     */
    @Action(EmissionFactorItemsAction.InvalidateData)
    invalidateData(ctx: StateContext<EmissionFactorItemsModel>) {
        ctx.patchState({
            emissionsFactorsByDimension: {},
            emissionFactorByDatabase: {},
        });
    }

    /**
     *
     * @param ctx Used to invalidate the state when changing the language
     */
    @Action(EmissionFactorItemsAction.InvalidateSpecificDimension)
    invalidateSpecificDimension(
        ctx: StateContext<EmissionFactorItemsModel>,
        action: EmissionFactorItemsAction.InvalidateSpecificDimension,
    ) {
        const copy = { ...ctx.getState().emissionsFactorsByDimension };
        delete copy[action.dimension];
        ctx.patchState({
            emissionsFactorsByDimension: copy,
        });
    }

    @Action(EmissionFactorItemsAction.InvalidateSpecificDatabase)
    invalidateSpecificDatabase(
        ctx: StateContext<EmissionFactorItemsModel>,
        action: EmissionFactorItemsAction.InvalidateSpecificDatabase,
    ) {
        const copy = { ...ctx.getState().emissionFactorByDatabase };
        delete copy[action.databaseId];
        ctx.patchState({
            emissionFactorByDatabase: copy,
        });
    }

    @Action(EmissionFactorItemsAction.CreateEmissionFactor)
    createEmissionFactor(
        ctx: StateContext<EmissionFactorItemsModel>,
        action: EmissionFactorItemsAction.CreateEmissionFactor,
    ) {
        this.emissionFactorHttpService
            .createEmissionFactor(action.emissionFactor, action.locale, action.organizationId)
            .subscribe((emissionFactorHierarchy) => {
                ctx.patchState({
                    emissionFactorByDatabase: {
                        ...ctx.getState().emissionFactorByDatabase,
                        [action.databaseId]: emissionFactorHierarchy,
                    },
                });
                action.callCompletion.next();
                this.store.dispatch(
                    new EmissionFactorItemsAction.InvalidateSpecificDimension(action.emissionFactor.dimension),
                );
                this.store.dispatch(new EmissionFactorItemsAction.SetEmissionFactorDatabases(action.organizationId));
            });
    }

    @Action(EmissionFactorItemsAction.UpdateEmissionFactor)
    updateEmissionFactor(
        ctx: StateContext<EmissionFactorItemsModel>,
        action: EmissionFactorItemsAction.UpdateEmissionFactor,
    ) {
        this.emissionFactorHttpService
            .updateEmissionFactor(action.emissionFactor, action.locale, action.organizationId)
            .subscribe((emissionFactorHierarchy) => {
                ctx.patchState({
                    emissionFactorByDatabase: {
                        ...ctx.getState().emissionFactorByDatabase,
                        [action.databaseId]: emissionFactorHierarchy,
                    },
                });
                action.callCompletion.next();
                this.store.dispatch(
                    new EmissionFactorItemsAction.InvalidateSpecificDimension(action.emissionFactor.dimension),
                );
            });
    }

    @Action(EmissionFactorItemsAction.DeleteEmissionFactor)
    deleteEmissionFactor(
        ctx: StateContext<EmissionFactorItemsModel>,
        action: EmissionFactorItemsAction.DeleteEmissionFactor,
    ) {
        this.emissionFactorHttpService
            .deleteEmissionFactor(action.emissionFactorId, action.databaseId, action.locale, action.organizationId)
            .subscribe((emissionFactorHierarchy) => {
                ctx.patchState({
                    emissionFactorByDatabase: {
                        ...ctx.getState().emissionFactorByDatabase,
                        [action.databaseId]: emissionFactorHierarchy,
                    },
                });
                action.callCompletion.next();
                this.store.dispatch(new EmissionFactorItemsAction.InvalidateSpecificDimension(action.rootDimension));
                this.store.dispatch(new EmissionFactorItemsAction.SetEmissionFactorDatabases(action.organizationId));
            });
    }

    @Action(EmissionFactorItemsAction.DeleteDimension)
    deleteDimension(ctx: StateContext<EmissionFactorItemsModel>, action: EmissionFactorItemsAction.DeleteDimension) {
        this.emissionFactorHttpService
            .deleteDimension(action.dimensionId, action.databaseId, action.locale, action.organizationId)
            .subscribe((emissionFactorHierarchy) => {
                ctx.patchState({
                    emissionFactorByDatabase: {
                        ...ctx.getState().emissionFactorByDatabase,
                        [action.databaseId]: emissionFactorHierarchy,
                    },
                });
                action.callCompletion.next();
            });
    }

    @Action(EmissionFactorItemsAction.CreateDimension)
    createDimension(ctx: StateContext<EmissionFactorItemsModel>, action: EmissionFactorItemsAction.CreateDimension) {
        this.emissionFactorHttpService
            .createDimension(action.dimension, action.locale, action.organizationId)
            .subscribe((emissionFactorHierarchy) => {
                if (emissionFactorHierarchy.id) {
                    ctx.patchState({
                        emissionFactorByDatabase: {
                            ...ctx.getState().emissionFactorByDatabase,
                            [emissionFactorHierarchy.id]: emissionFactorHierarchy,
                        },
                    });
                }
                action.callCompletion.next();
            });
    }

    @Action(EmissionFactorItemsAction.UpdateDimension)
    updateDimension(ctx: StateContext<EmissionFactorItemsModel>, action: EmissionFactorItemsAction.UpdateDimension) {
        this.emissionFactorHttpService
            .updateDimension(action.dimension, action.locale, action.organizationId)
            .subscribe((emissionFactorHierarchy) => {
                ctx.patchState({
                    emissionFactorByDatabase: {
                        ...ctx.getState().emissionFactorByDatabase,
                        [emissionFactorHierarchy.id]: emissionFactorHierarchy,
                    },
                });
                action.callCompletion.next();
                this.store.dispatch(new EmissionFactorItemsAction.InvalidateSpecificDimension(action.rootDimension));
            });
    }

    // Emission factor databases //

    @Action(EmissionFactorItemsAction.SetEmissionFactorDatabases)
    setEmissionFactorDatabases(
        ctx: StateContext<EmissionFactorItemsModel>,
        action: EmissionFactorItemsAction.SetEmissionFactorDatabases,
    ) {
        this.emissionFactorHttpService
            .getEmissionFactorDatabasesByOrganization(action.organizationId)
            .subscribe((databases) => {
                ctx.patchState({
                    emissionFactorDatabases: databases,
                });
            });
    }

    @Action(EmissionFactorItemsAction.CreateEmissionFactorDatabase)
    CreateEmissionFactorDatabase(
        ctx: StateContext<EmissionFactorItemsModel>,
        action: EmissionFactorItemsAction.CreateEmissionFactorDatabase,
    ) {
        this.emissionFactorHttpService.createEmissionFactorDatabase(action.organizationId, action.database).subscribe({
            next: (databaseId) => {
                action.database.id = databaseId;
                const currentDatabases = ctx.getState().emissionFactorDatabases || [];
                action.database.count = 0;
                ctx.patchState({
                    emissionFactorDatabases: [...currentDatabases, action.database],
                });
                action.callCompletion.next();
            },
            error: () => {
                action.callCompletion.error(undefined);
            },
        });
    }

    @Action(EmissionFactorItemsAction.UpdateEmissionFactorDatabase)
    UpdateEmissionFactorDatabase(
        ctx: StateContext<EmissionFactorItemsModel>,
        action: EmissionFactorItemsAction.UpdateEmissionFactorDatabase,
    ) {
        this.emissionFactorHttpService.updateEmissionFactorDatabase(action.organizationId, action.database).subscribe({
            next: () => {
                const currentDatabases = ctx.getState().emissionFactorDatabases || [];
                const updatedDatabases = currentDatabases.map((db) =>
                    db.id === action.database.id ? action.database : db,
                );
                ctx.patchState({
                    emissionFactorDatabases: updatedDatabases,
                });
                action.callCompletion.next();
            },
            error: () => {
                action.callCompletion.error(undefined);
            },
        });
    }

    @Action(EmissionFactorItemsAction.DeleteEmissionFactorDatabase)
    DeleteEmissionFactorDatabase(
        ctx: StateContext<EmissionFactorItemsModel>,
        action: EmissionFactorItemsAction.DeleteEmissionFactorDatabase,
    ) {
        this.emissionFactorHttpService
            .deleteEmissionFactorDatabase(action.organizationId, action.databaseId)
            .subscribe({
                next: () => {
                    const currentDatabases = ctx.getState().emissionFactorDatabases || [];
                    const updatedDatabases = currentDatabases.filter((db) => db.id !== action.databaseId);
                    ctx.patchState({
                        emissionFactorDatabases: updatedDatabases,
                    });
                    action.callCompletion.next();
                },
            });
    }

    @Action(EmissionFactorItemsAction.InvalidateAllDatabases)
    invalidateAllDatabases(ctx: StateContext<EmissionFactorItemsModel>) {
        ctx.patchState({
            emissionFactorByDatabase: {},
        });
    }

    static getEmissionsFactorsByDimensionId(dimensionId: string) {
        return createSelector([EmissionFactorItemsState], (state: EmissionFactorItemsModel) => {
            return state?.emissionsFactorsByDimension?.[dimensionId];
        });
    }

    static getEmissionFactorByDatabaseId(databaseId: string) {
        return createSelector([EmissionFactorItemsState], (state: EmissionFactorItemsModel) => {
            return state?.emissionFactorByDatabase?.[databaseId];
        });
    }

    @Selector([EmissionFactorItemsState])
    static getOfficialCategories(state: EmissionFactorItemsModel) {
        return state.officialCategories;
    }

    @Selector([EmissionFactorItemsState])
    static getEmissionFactorDatabases(state: EmissionFactorItemsModel) {
        return state.emissionFactorDatabases;
    }
}
